/**
 * O validador está aberto em outra aba (ou janela) deste navegador.
 *
 * O banco local só aceita um dono por vez, então duas abas não podem validar
 * juntas. Em vez de um erro sem saída, o operador escolhe — como no WhatsApp
 * Web: usar aqui (a outra termina o que está gravando e passa o banco) ou
 * voltar para a outra.
 *
 * As validações não são de aba nenhuma: estão no banco, que é o mesmo. Quem
 * assume encontra a contagem e a fila de envio exatamente onde estavam.
 */

import React, {useState} from 'react';
import {ActivityIndicator, Pressable, Text, View} from 'react-native';
import {AppWindow, MonitorSmartphone} from 'lucide-react-native';

import {Marca} from '../ui/Marca';
import {txt, useIdioma} from '../ui/idioma';
import {estilos, fontes, useTema} from '../ui/tema';

export function TelaOutraAba({
  assumida,
  aoUsarAqui,
}: {
  /** true quando esta aba tinha o validador e outra o pegou. */
  assumida: boolean;
  /** Pede o banco à outra aba. Lança com a mensagem se ela não soltar. */
  aoUsarAqui: () => Promise<void>;
}) {
  const t = useTema();
  useIdioma();
  const [assumindo, setAssumindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [podeFechar, setPodeFechar] = useState(false);

  async function usarAqui() {
    setErro(null);
    setAssumindo(true);
    try {
      await aoUsarAqui();
    } catch (e) {
      setErro((e as Error).message);
      setAssumindo(false);
    }
  }

  /**
   * O navegador só deixa o script fechar a janela que o script abriu. Na
   * maioria das vezes esta aba foi aberta pelo operador, então o `close` não
   * faz nada — e aí a tela diz o que fazer, em vez de o botão parecer quebrado.
   */
  function continuarNaOutra() {
    window.close();
    setTimeout(() => setPodeFechar(true), 300);
  }

  return (
    <View style={{flex: 1, backgroundColor: t.fundo, alignItems: 'center', justifyContent: 'center', padding: 24}}>
      <View style={{width: '100%', maxWidth: 440, gap: 22}}>
        <Marca largura={110} />

        <View
          style={{
            width: 64,
            height: 64,
            borderRadius: 18,
            backgroundColor: t.superficie,
            borderWidth: 1,
            borderColor: t.borda,
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <MonitorSmartphone size={30} color={t.primaria} strokeWidth={2} />
        </View>

        <View style={{gap: 8}}>
          <Text style={[estilos.titulo, {color: t.texto, fontSize: 26, lineHeight: 31}]}>
            {assumida
              ? txt('O validador foi aberto em outra aba')
              : txt('O validador já está aberto em outra aba')}
          </Text>
          <Text style={[estilos.subtitulo, {color: t.textoMedio}]}>
            {txt(
              'Ele funciona em uma aba por vez neste navegador. As validações ficam guardadas e seguem de onde pararam, em qualquer uma das duas.',
            )}
          </Text>
        </View>

        {erro ? (
          <Text style={[estilos.erro, {color: t.erro}]}>{erro}</Text>
        ) : null}

        {podeFechar ? (
          <Text style={[estilos.subtitulo, {color: t.textoFraco}]}>
            {txt('Pode fechar esta aba — o validador continua na outra.')}
          </Text>
        ) : null}

        <View style={{gap: 10}}>
          <Pressable
            onPress={usarAqui}
            disabled={assumindo}
            style={[estilos.botao, {backgroundColor: t.primaria}]}>
            {assumindo ? (
              <ActivityIndicator color={t.tintaPrimaria} />
            ) : (
              <>
                <AppWindow size={20} color={t.tintaPrimaria} strokeWidth={2.4} />
                <Text style={[estilos.textoBotao, {color: t.tintaPrimaria}]}>{txt('Usar aqui')}</Text>
              </>
            )}
          </Pressable>

          <Pressable
            onPress={continuarNaOutra}
            disabled={assumindo}
            style={[estilos.botaoSecundario, {backgroundColor: t.superficie, borderColor: t.borda}]}>
            <Text style={[estilos.textoBotaoSecundario, {color: t.textoMedio, fontFamily: fontes.semi}]}>
              {txt('Continuar na outra aba')}
            </Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
}
